import React, {FC, useState, useCallback, useMemo} from 'react'
import moment from 'moment'
import {OverlayTrigger, Tooltip, Spinner} from 'react-bootstrap'
import {toast} from 'react-toastify'
import styles from './RightPanel.module.scss'
import TagsModal from './TagsModal'
import AddNoteModal from './AddNoteModal'
import EditSingleSubscriber from '../../../Contacts/AllContactsList/Modal/EditSingleSubscriber'
import {useTasks} from '../../hooks/useTasks'
import {useNotes, NOTE_TYPE_ICONS} from '../../hooks/useNotes'
import {useAISummary} from '../../hooks/useAISummary'
import {useContactTags} from '../../hooks/useContactTags'
import {useActivityLog, ACTIVITY_TYPE_ICONS} from '../../hooks/useActivityLog'
import {
  TaskItem,
  ContactDetail,
  NoteVoiceContent,
  TaskLinkedContact,
} from '../../../../../../services/cellcast.v2.types'
import {useLazyGetContactInformationQuery} from '../../../../../../services/cellcast.v2'
import TaskFormModal, {TaskFormValues} from '../../../contact-collection/tasks/TaskFormModal'
import {ContactDetails, ContactStats, RightPanelTab} from '../../types/inbox-v3.types'

// Helper for error message extraction
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const resolveErrorMessage = (error: unknown): string => {
  if (!isRecord(error)) {
    return 'Something went wrong'
  }
  const dataField = error.data
  if (isRecord(dataField)) {
    const message = dataField.message
    if (typeof message === 'string' && message.trim().length > 0) {
      return message.trim()
    }
  }
  if ('message' in error && typeof error.message === 'string' && error.message.trim().length > 0) {
    return error.message.trim()
  }
  return 'Unable to complete the request'
}

interface RightPanelProps {
  contactDetails: ContactDetails | null
  contactStats: ContactStats | null
  activeTab: RightPanelTab
  onTabChange: (tab: RightPanelTab) => void
  isLoading: boolean
  isMobile: boolean
  onClose?: () => void
  onTagsUpdated?: () => void
}

const RightPanel: FC<RightPanelProps> = ({
  contactDetails,
  contactStats,
  activeTab,
  onTabChange,
  isLoading,
  isMobile,
  onClose,
  onTagsUpdated,
}) => {
  // State for tags modal
  const [showTagsModal, setShowTagsModal] = useState(false)
  const [isRemovingTag, setIsRemovingTag] = useState<string | null>(null)

  // State for edit contact modal - matches EditSingleSubscriber expected props
  const [editModal, setEditModal] = useState<{
    data: any
    toggle: boolean
    number: string
    _id?: string
    tags: any[]
    group?: any
    fields?: any
    isValidateNumber: boolean
  }>({
    data: [],
    toggle: false,
    number: '',
    _id: '',
    tags: [],
    group: undefined,
    fields: [],
    isValidateNumber: false,
  })

  // State for task modal
  const [showTaskModal, setShowTaskModal] = useState(false)
  const [editingTask, setEditingTask] = useState<TaskItem | null>(null)
  const [isLoadingEditContact, setIsLoadingEditContact] = useState(false)
  const [isMessageActivityCollapsed, setIsMessageActivityCollapsed] = useState(false)

  // RTK Query hook for fetching full contact details
  const [getContactData] = useLazyGetContactInformationQuery()

  // Use the contact tags hook for removing tags
  const {removeTagsFromContact} = useContactTags({
    contactId: contactDetails?._id || null,
    currentTags: contactDetails?.tags || [],
    onTagsUpdated: onTagsUpdated,
  })

  // Use the tasks hook - filter by contact ID (contact group map ID)
  const {
    tasks,
    isLoading: isLoadingTasks,
    createTask,
    updateTask,
    completeTask,
    deleteTask,
    isSubmitting: isSubmittingTask,
  } = useTasks({
    contactId: contactDetails?._id,
    enabled: !!contactDetails?._id && activeTab === 'tasks',
  })

  // Use the activity log hook
  const {
    groupedActivities,
    isLoading: isLoadingActivities,
    getActivityIcon,
  } = useActivityLog({
    phoneNumber: contactDetails?.number,
    enabled: !!contactDetails?.number && activeTab === 'activity',
  })

  // Use the notes hook
  const {
    groupedNotes,
    isLoading: isLoadingNotes,
    createNote,
    isSubmitting: isSubmittingNote,
    getNoteIcon,
    getNoteText,
    isVoiceNote,
  } = useNotes({
    phoneNumber: contactDetails?.number,
    enabled: !!contactDetails?.number && activeTab === 'note',
  })

  // Use the AI summary hook - only for wallet contacts
  const {
    aiSummaryNote,
    hasAISummary,
    isLoading: isLoadingAISummary,
  } = useAISummary({
    phoneNumber: contactDetails?.number,
    contactSource: contactDetails?.addedThrough,
    enabled: !!contactDetails?.number,
  })

  // State for note modal
  const [showNoteModal, setShowNoteModal] = useState(false)

  // Memoize preSelectedContact as a task template to pre-populate contacts when creating a new task
  // We pass this as initialTask (without id/_id) so TaskFormModal treats it as "Create Task" but pre-fills contacts
  const preSelectedContactTaskTemplate = useMemo(() => {
    if (!contactDetails) return null
    const contactName =
      contactDetails.fullName ||
      `${contactDetails.firstName || ''} ${contactDetails.lastName || ''}`.trim()
    const linkedContact: TaskLinkedContact = {
      id: contactDetails._id,
      fullName: contactName,
      number: contactDetails.number,
    }
    // Return a partial TaskItem with contacts pre-filled but NO id/_id
    // This makes TaskFormModal show "Create Task" while pre-populating the contacts field
    return {
      title: '',
      contacts: [linkedContact],
    } as Partial<TaskItem>
  }, [
    contactDetails?._id,
    contactDetails?.fullName,
    contactDetails?.firstName,
    contactDetails?.lastName,
    contactDetails?.number,
  ])

  // Handle add note
  const handleAddNote = useCallback(
    async (noteText: string): Promise<boolean> => {
      const success = await createNote(noteText)
      if (success) {
        toast.success('Note added successfully')
        return true
      } else {
        toast.error('Failed to add note')
        return false
      }
    },
    [createNote]
  )

  // Get initials
  const getInitials = () => {
    if (!contactDetails) return '?'
    const {firstName, lastName, number} = contactDetails
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
    }
    if (firstName) {
      return firstName.charAt(0).toUpperCase()
    }
    return number.slice(-2)
  }

  // Get display name
  const getDisplayName = () => {
    if (!contactDetails) return ''
    const {firstName, lastName, fullName, number} = contactDetails
    return fullName || `${firstName} ${lastName}`.trim() || number
  }

  // Get visible tags
  const getVisibleTags = () => {
    if (!contactDetails?.tags) return []
    return contactDetails.tags.filter((tag) => !tag.isHidden)
  }

  // Task handlers
  const handleAddTask = useCallback(() => {
    setEditingTask(null)
    setShowTaskModal(true)
  }, [])

  // Handle remove single tag
  const handleRemoveTag = useCallback(
    async (tagId: string) => {
      if (!tagId) return
      setIsRemovingTag(tagId)
      try {
        await removeTagsFromContact([tagId])
      } finally {
        setIsRemovingTag(null)
      }
    },
    [removeTagsFromContact]
  )

  // Handle edit contact - fetch full contact data first
  const handleEditContact = useCallback(async () => {
    if (!contactDetails?._id) return

    setIsLoadingEditContact(true)
    try {
      const res = await getContactData(contactDetails._id)
      if ('data' in res && res.data?.data) {
        const contactData: ContactDetail = res.data.data

        // Build finalFieldData like ContactDetailPanel does
        const mergedArray: string[] = contactData?.group?.fields
          ? [...contactData.group.fields]
          : []

        const finalFieldData = mergedArray.map((key: string) => {
          const found = contactData?.fields?.find((val: any) => val?.name === key)
          if (found !== undefined) {
            return found
          } else {
            return {name: key, value: ''}
          }
        })

        setEditModal({
          data: finalFieldData,
          toggle: true,
          number: contactData?.number || '',
          _id: contactData?._id,
          tags: contactData?.tags || [],
          group: contactData?.group,
          fields: finalFieldData,
          isValidateNumber: false,
        })
      }
    } catch (error) {
      console.error('Error fetching contact details for edit:', error)
    } finally {
      setIsLoadingEditContact(false)
    }
  }, [contactDetails?._id, getContactData])

  const handleEditTask = useCallback((task: TaskItem) => {
    setEditingTask(task)
    setShowTaskModal(true)
  }, [])

  const handleTaskSubmit = useCallback(
    async (values: TaskFormValues) => {
      try {
        if (editingTask) {
          // Update existing task
          const result = await updateTask(editingTask.id, {
            title: values.title,
            description: values.description,
            dueDate: values.dueDate,
            priority: values.priority,
            assignedTo: values.assignedTo,
            setUpReminder: values.setUpReminder,
            reminderMinutesBeforeDue: values.reminderMinutesBeforeDue,
            contacts: values.contacts,
            leads: values.leads,
          })
          if (result) {
            toast.success('Task updated successfully')
            setShowTaskModal(false)
            setEditingTask(null)
          } else {
            toast.error('Failed to update task')
          }
        } else {
          // Create new task
          const result = await createTask(values)
          if (result) {
            toast.success('Task created successfully')
            setShowTaskModal(false)
          } else {
            toast.error('Failed to create task')
          }
        }
      } catch (error) {
        toast.error(resolveErrorMessage(error))
      }
    },
    [editingTask, createTask, updateTask]
  )

  const handleToggleTaskComplete = useCallback(
    async (task: TaskItem) => {
      const taskId = task.id
      if (!taskId) return

      try {
        if (task.status === 'completed') {
          // If already completed, update status back to pending
          const result = await updateTask(taskId, {status: 'pending'})
          if (result) {
            toast.success('Task moved back to pending')
          } else {
            toast.error('Failed to update task')
          }
        } else {
          // Mark as complete
          const result = await completeTask(taskId)
          if (result) {
            toast.success('Task marked as completed')
          } else {
            toast.error('Failed to complete task')
          }
        }
      } catch (error) {
        toast.error(resolveErrorMessage(error))
      }
    },
    [completeTask, updateTask]
  )

  const handleDeleteTask = useCallback(
    async (taskId: string) => {
      if (window.confirm('Are you sure you want to delete this task?')) {
        try {
          const result = await deleteTask(taskId)
          if (result) {
            toast.success('Task deleted')
          } else {
            toast.error('Failed to delete task')
          }
        } catch (error) {
          toast.error(resolveErrorMessage(error))
        }
      }
    },
    [deleteTask]
  )

  // Format task due date
  const formatTaskDate = (dateString: string) => {
    const date = moment(dateString)
    if (date.isSame(moment(), 'day')) return 'Today'
    if (date.isSame(moment().add(1, 'day'), 'day')) return 'Tomorrow'
    if (date.isSame(moment().subtract(1, 'day'), 'day')) return 'Yesterday'
    return date.format('MMM D, YYYY')
  }

  // Get task status style
  const getTaskStatusClass = (task: TaskItem) => {
    if (task.status === 'completed') return styles.statusDone
    const dueDate = moment(task.dueDate)
    if (dueDate.isBefore(moment(), 'day')) return styles.statusOverdue
    if (dueDate.isSame(moment(), 'day')) return styles.statusDueToday
    return styles.statusTodo
  }

  // Get task status label
  const getTaskStatusLabel = (task: TaskItem) => {
    if (task.status === 'completed') return 'Done'
    const dueDate = moment(task.dueDate)
    if (dueDate.isBefore(moment(), 'day')) return 'Overdue'
    if (dueDate.isSame(moment(), 'day')) return 'Due Today'
    return 'Pending'
  }

  // Get priority badge class
  const getPriorityClass = (priority: string) => {
    switch (priority) {
      case 'high':
        return styles.priorityHigh
      case 'medium':
        return styles.priorityMedium
      case 'low':
        return styles.priorityLow
      default:
        return ''
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <div className={styles.rightPanelContainer}>
        <div className={styles.loadingState}>
          <div className={`${styles.skeletonLine} ${styles.wide}`}></div>
          <div className={`${styles.skeletonLine} ${styles.medium}`}></div>
          <div className={`${styles.skeletonLine} ${styles.short}`}></div>
        </div>
      </div>
    )
  }

  // Empty state
  if (!contactDetails) {
    return (
      <div className={styles.rightPanelContainer}>
        <div className={styles.emptyState}>
          <i className='bi bi-person-circle'></i>
          <h4>No contact selected</h4>
          <p>Select a conversation to view contact details</p>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.rightPanelContainer}>
      {/* Mobile Header */}
      {isMobile && (
        <div className={styles.mobileHeader}>
          <span className={styles.title}>Details</span>
          <button className={styles.closeButton} onClick={onClose}>
            <i className='bi bi-x-lg'></i>
          </button>
        </div>
      )}

      {/* Contact Info Section */}
      <div className={styles.contactInfoSection}>
        {/* Avatar and Name */}
        <div className={styles.contactHeader}>
          <div className={styles.contactAvatar}>{getInitials()}</div>
          <div className={styles.contactInfo}>
            <div className={styles.nameRow}>
              <div className={styles.contactName}>{getDisplayName()}</div>
              <button
                className={styles.editIconButton}
                onClick={handleEditContact}
                disabled={isLoadingEditContact}
                title='Edit Contact'
              >
                {isLoadingEditContact ? (
                  <Spinner animation='border' size='sm' />
                ) : (
                  <i className='bi bi-pencil'></i>
                )}
              </button>
            </div>
            <div className={styles.contactMeta}>
              {contactDetails.email && (
                <span className={styles.metaItem}>
                  <i className='bi bi-envelope'></i>
                  {contactDetails.email}
                </span>
              )}
              <span className={styles.metaItem}>
                <i className='bi bi-telephone'></i>
                {contactDetails.number}
              </span>
            </div>
          </div>
        </div>

        {/* Tags Section */}
        <div className={styles.tagsContainer}>
          <div className={styles.tagsHeader}>
            <span className={styles.tagsLabel}>TAGS</span>
            <button
              className={styles.addTagButton}
              onClick={() => setShowTagsModal(true)}
              title='Add Tags'
            >
              <i className='bi bi-plus-lg'></i>
            </button>
          </div>
          {getVisibleTags().length > 0 ? (
            <div className={styles.contactTags}>
              {getVisibleTags().map((tag, idx) => (
                <span key={tag._id || idx} className={styles.tag}>
                  {tag.name}
                  <button
                    className={styles.removeTagButton}
                    onClick={() => tag._id && handleRemoveTag(tag._id)}
                    disabled={isRemovingTag === tag._id}
                    title='Remove tag'
                  >
                    {isRemovingTag === tag._id ? (
                      <Spinner animation='border' size='sm' />
                    ) : (
                      <i className='bi bi-x'></i>
                    )}
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <span className={styles.noTags}>No tags assigned</span>
          )}
        </div>

        {/* AI Summary Card - Only shown for wallet contacts with AI summary */}
        {hasAISummary && (
          <div className={styles.aiSummaryCard}>
            <div className={styles.aiSummaryHeader}>
              <i className='bi bi-stars'></i>
              <span>AI Summary</span>
            </div>
            <div className={styles.aiSummaryContent}>
              {isLoadingAISummary ? (
                <div className={styles.aiSummaryLoading}>
                  <Spinner animation='border' size='sm' />
                  <span>Loading AI summary...</span>
                </div>
              ) : (
                aiSummaryNote
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className={styles.tabsSection}>
        <OverlayTrigger placement='top' overlay={<Tooltip id='tab-detail'>Detail</Tooltip>}>
          <button
            className={`${styles.tab} ${activeTab === 'detail' ? styles.active : ''}`}
            onClick={() => onTabChange('detail')}
          >
            <i className='bi bi-info-circle'></i>
          </button>
        </OverlayTrigger>
        <OverlayTrigger placement='top' overlay={<Tooltip id='tab-tasks'>Tasks</Tooltip>}>
          <button
            className={`${styles.tab} ${activeTab === 'tasks' ? styles.active : ''}`}
            onClick={() => onTabChange('tasks')}
          >
            <i className='bi bi-check2-square'></i>
          </button>
        </OverlayTrigger>
        <OverlayTrigger placement='top' overlay={<Tooltip id='tab-note'>Note</Tooltip>}>
          <button
            className={`${styles.tab} ${activeTab === 'note' ? styles.active : ''}`}
            onClick={() => onTabChange('note')}
          >
            <i className='bi bi-journal-text'></i>
          </button>
        </OverlayTrigger>
        <OverlayTrigger placement='top' overlay={<Tooltip id='tab-activity'>Activity</Tooltip>}>
          <button
            className={`${styles.tab} ${activeTab === 'activity' ? styles.active : ''}`}
            onClick={() => onTabChange('activity')}
          >
            <i className='bi bi-clock-history'></i>
          </button>
        </OverlayTrigger>
      </div>

      {/* Tab Content */}
      <div className={styles.tabContent}>
        {/* DETAIL TAB */}
        {activeTab === 'detail' && (
          <>
            {/* Contact Information Section */}
            <div className={styles.sectionTitle}>Contact Information</div>
            <div className={styles.contactInfoList}>
              {/* Mobile Number */}
              <div className={styles.infoRow}>
                <div className={styles.infoLabel}>
                  <i className='bi bi-telephone'></i>
                  Mobile
                </div>
                <div className={styles.infoValue}>{contactDetails?.number || '-'}</div>
              </div>

              {/* First Name */}
              {contactDetails?.firstName && (
                <div className={styles.infoRow}>
                  <div className={styles.infoLabel}>
                    <i className='bi bi-person'></i>
                    First Name
                  </div>
                  <div className={styles.infoValue}>{contactDetails.firstName}</div>
                </div>
              )}

              {/* Last Name */}
              {contactDetails?.lastName && (
                <div className={styles.infoRow}>
                  <div className={styles.infoLabel}>
                    <i className='bi bi-person'></i>
                    Last Name
                  </div>
                  <div className={styles.infoValue}>{contactDetails.lastName}</div>
                </div>
              )}

              {/* Email */}
              {contactDetails?.email && (
                <div className={styles.infoRow}>
                  <div className={styles.infoLabel}>
                    <i className='bi bi-envelope'></i>
                    Email
                  </div>
                  <div className={styles.infoValue}>{contactDetails.email}</div>
                </div>
              )}
            </div>

            {/* Status Section */}
            <div className={styles.sectionTitle}>Status</div>
            <div className={styles.statusSection}>
              <div className={styles.statusRow}>
                <div className={styles.statusLabel}>Subscription</div>
                <div
                  className={`${styles.statusBadge} ${
                    contactDetails?.isBlocked || contactDetails?.isBlockedByUser
                      ? styles.unsubscribed
                      : styles.subscribed
                  }`}
                >
                  <i
                    className={`bi ${
                      contactDetails?.isBlocked || contactDetails?.isBlockedByUser
                        ? 'bi-x-circle-fill'
                        : 'bi-check-circle-fill'
                    }`}
                  ></i>
                  {contactDetails?.isBlocked || contactDetails?.isBlockedByUser
                    ? 'Unsubscribed'
                    : 'Subscribed'}
                </div>
              </div>
              <div className={styles.statusRow}>
                <div className={styles.statusLabel}>Date Added</div>
                <div className={styles.statusValue}>
                  {contactDetails?.createdAt
                    ? moment(contactDetails.createdAt).format('DD MMM YYYY')
                    : '-'}
                </div>
              </div>
              <div className={styles.statusRow}>
                <div className={styles.statusLabel}>Contact Source</div>
                <div className={styles.statusValue}>{contactDetails?.addedThrough || '-'}</div>
              </div>
            </div>

            {/* Message Activity */}
            {contactStats && (
              <>
                <div
                  className={styles.sectionTitleCollapsible}
                  onClick={() => setIsMessageActivityCollapsed(!isMessageActivityCollapsed)}
                >
                  <span>Message Activity</span>
                  <i
                    className={`bi ${
                      isMessageActivityCollapsed ? 'bi-chevron-down' : 'bi-chevron-up'
                    }`}
                  ></i>
                </div>
                {!isMessageActivityCollapsed && (
                  <div className={styles.statsGrid}>
                    <div className={styles.statCard}>
                      <div className={styles.statValue}>{contactStats.total}</div>
                      <div className={styles.statLabel}>Total Messages</div>
                    </div>
                    <div className={styles.statCard}>
                      <div className={styles.statValue}>{contactStats.outbound}</div>
                      <div className={styles.statLabel}>Outbound</div>
                    </div>
                    <div className={styles.statCard}>
                      <div className={styles.statValue}>{contactStats.inbound}</div>
                      <div className={styles.statLabel}>Inbound</div>
                    </div>
                    <div className={styles.statCard}>
                      <div className={styles.statValue}>{contactStats.sms}</div>
                      <div className={styles.statLabel}>SMS</div>
                    </div>
                    <div className={styles.statCard}>
                      <div className={styles.statValue}>{contactStats.mms}</div>
                      <div className={styles.statLabel}>MMS</div>
                    </div>
                    <div className={styles.statCard}>
                      <div className={styles.statValue}>{contactStats.unread}</div>
                      <div className={styles.statLabel}>Unread</div>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* TASKS TAB */}
        {activeTab === 'tasks' && (
          <div className={styles.tasksContainer}>
            <div className={styles.tabHeader}>
              <h3>Tasks</h3>
              <button className={styles.addButton} onClick={handleAddTask}>
                <i className='bi bi-plus-lg'></i>
              </button>
            </div>

            {isLoadingTasks && (
              <div className={styles.loadingState}>
                <Spinner animation='border' size='sm' />
                <span>Loading tasks...</span>
              </div>
            )}

            {!isLoadingTasks && (
              <div className={styles.tasksList}>
                {tasks.length === 0 ? (
                  <div className={styles.emptyState}>
                    <i className='bi bi-check2-square'></i>
                    <p>No tasks yet</p>
                    <button className={styles.addTaskButton} onClick={handleAddTask}>
                      Add Task
                    </button>
                  </div>
                ) : (
                  tasks.map((task) => (
                    <div
                      key={task.id}
                      className={`${styles.taskItem} ${
                        task.status === 'completed' ? styles.completed : ''
                      }`}
                    >
                      <div className={styles.taskCheckbox}>
                        <input
                          type='checkbox'
                          checked={task.status === 'completed'}
                          onChange={() => handleToggleTaskComplete(task)}
                          disabled={isSubmittingTask}
                        />
                      </div>
                      <div className={styles.taskContent} onClick={() => handleEditTask(task)}>
                        <div className={styles.taskTitle}>{task.title}</div>
                        {task.description && (
                          <div className={styles.taskDescription}>{task.description}</div>
                        )}
                        <div className={styles.taskMeta}>
                          <span className={styles.taskDate}>
                            <i className='bi bi-calendar3'></i> {formatTaskDate(task.dueDate)}
                          </span>
                          <span className={`${styles.taskStatus} ${getTaskStatusClass(task)}`}>
                            {getTaskStatusLabel(task)}
                          </span>
                          {task.priority && (
                            <span
                              className={`${styles.taskPriority} ${getPriorityClass(
                                task.priority
                              )}`}
                            >
                              {task.priority}
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        className={styles.taskDeleteBtn}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteTask(task.id)
                        }}
                        title='Delete task'
                      >
                        <i className='bi bi-trash'></i>
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {/* NOTE TAB */}
        {activeTab === 'note' && (
          <div className={styles.notesContainer}>
            <div className={styles.tabHeader}>
              <h3>Notes</h3>
              <button
                className={styles.addButton}
                onClick={() => setShowNoteModal(true)}
                title='Add note'
              >
                <i className='bi bi-plus-lg'></i>
              </button>
            </div>

            {/* Notes List */}
            {isLoadingNotes ? (
              <div className={styles.loadingState}>
                <Spinner animation='border' size='sm' />
                <span>Loading notes...</span>
              </div>
            ) : groupedNotes.length === 0 ? (
              <div className={styles.emptyState}>
                <i className='bi bi-journal-text'></i>
                <h4>No notes yet</h4>
                <p>Add a note to keep track of important information</p>
              </div>
            ) : (
              groupedNotes.map((group) => (
                <div key={group.date} className={styles.notesGroup}>
                  <div className={styles.dateLabel}>{group.dateLabel}</div>
                  {group.items.map((note) => {
                    const iconConfig = getNoteIcon(note.type)
                    const voiceNote = isVoiceNote(note)
                    return (
                      <div key={note.id} className={styles.noteItem}>
                        <div className={styles.noteHeader}>
                          <div className={styles.noteTypeIcon}>
                            <div
                              className={styles.noteIcon}
                              style={{
                                backgroundColor: `${iconConfig.color}20`,
                                color: iconConfig.color,
                              }}
                            >
                              <i className={`bi ${iconConfig.icon}`}></i>
                            </div>
                            <span className={styles.noteType}>
                              {note.type === 'voice' ? 'Voice Note' : 'Manual Note'}
                            </span>
                          </div>
                          <span className={styles.noteDate}>
                            {moment(note.createdAt).format('h:mm A')}
                          </span>
                        </div>
                        <div className={styles.noteContent}>{getNoteText(note)}</div>
                        {voiceNote && (note.content as NoteVoiceContent).sentiment && (
                          <div className={styles.noteMeta}>
                            <span
                              className={`${styles.noteBadge} ${
                                (note.content as NoteVoiceContent).sentiment === 'Positive'
                                  ? styles.positive
                                  : (note.content as NoteVoiceContent).sentiment === 'Negative'
                                    ? styles.negative
                                    : styles.neutral
                              }`}
                            >
                              {(note.content as NoteVoiceContent).sentiment}
                            </span>
                            {(note.content as NoteVoiceContent).leadIntent && (
                              <span className={styles.noteBadge}>
                                {(note.content as NoteVoiceContent).leadIntent}
                              </span>
                            )}
                          </div>
                        )}
                        {voiceNote &&
                          (note.content as NoteVoiceContent).highlights &&
                          (note.content as NoteVoiceContent).highlights.length > 0 && (
                            <div className={styles.noteHighlights}>
                              <span className={styles.highlightsLabel}>Highlights:</span>
                              <ul>
                                {(note.content as NoteVoiceContent).highlights.map(
                                  (highlight, idx) => (
                                    <li key={idx}>{highlight}</li>
                                  )
                                )}
                              </ul>
                            </div>
                          )}
                        {voiceNote &&
                          (note.content as NoteVoiceContent).actionItems &&
                          (note.content as NoteVoiceContent).actionItems.length > 0 && (
                            <div className={styles.noteActionItems}>
                              <span className={styles.actionItemsLabel}>Action Items:</span>
                              <ul>
                                {(note.content as NoteVoiceContent).actionItems.map((item, idx) => (
                                  <li key={idx}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                      </div>
                    )
                  })}
                </div>
              ))
            )}
          </div>
        )}

        {/* ACTIVITY TAB */}
        {activeTab === 'activity' && (
          <div className={styles.activityContainer}>
            {isLoadingActivities ? (
              <div className={styles.loadingState}>
                <Spinner animation='border' size='sm' />
                <span>Loading activity...</span>
              </div>
            ) : groupedActivities.length === 0 ? (
              <div className={styles.emptyState}>
                <i className='bi bi-clock-history'></i>
                <h4>No activity yet</h4>
                <p>Activity will appear here once there are interactions</p>
              </div>
            ) : (
              groupedActivities.map((group) => (
                <div key={group.date} className={styles.activityGroup}>
                  <div className={styles.dateLabel}>{group.dateLabel}</div>
                  {group.items.map((item) => {
                    const iconConfig = getActivityIcon(item.activityType)
                    return (
                      <div key={item.id} className={styles.activityItem}>
                        <div
                          className={styles.activityIcon}
                          style={{
                            backgroundColor: `${iconConfig.color}20`,
                            color: iconConfig.color,
                          }}
                        >
                          <i className={`bi ${iconConfig.icon}`}></i>
                        </div>
                        <div className={styles.activityContent}>
                          <div className={styles.activityTitle}>{item.requestName}</div>
                          <div className={styles.activityDescription}>{item.content}</div>
                          {item.performedBy && (
                            <div className={styles.activityPerformer}>
                              by{' '}
                              {item.performedBy.firstName ||
                                item.performedBy.name ||
                                item.performedBy.email}
                            </div>
                          )}
                        </div>
                        <div className={styles.activityTime}>
                          {moment(item.sendTime || item.createdAt).format('h:mm A')}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Tags Modal */}
      <TagsModal
        show={showTagsModal}
        onHide={() => setShowTagsModal(false)}
        contactId={contactDetails?._id || null}
        currentTags={contactDetails?.tags || []}
        onTagsUpdated={() => {
          setShowTagsModal(false)
          onTagsUpdated?.()
        }}
      />

      {/* Edit Contact Modal - Reusing EditSingleSubscriber from contacts page */}
      <EditSingleSubscriber
        show={editModal}
        handleClose={() => setEditModal({...editModal, toggle: false})}
        callbackFn={() => {
          onTagsUpdated?.()
        }}
      />

      {/* Task Form Modal */}
      <TaskFormModal
        show={showTaskModal}
        onClose={() => {
          setShowTaskModal(false)
          setEditingTask(null)
        }}
        onSubmit={handleTaskSubmit}
        isSubmitting={isSubmittingTask}
        initialTask={editingTask || (preSelectedContactTaskTemplate as TaskItem | null)}
      />

      {/* Add Note Modal */}
      <AddNoteModal
        show={showNoteModal}
        onHide={() => setShowNoteModal(false)}
        onSubmit={handleAddNote}
        isSubmitting={isSubmittingNote}
      />
    </div>
  )
}

export default RightPanel